import { Ionicons } from '@expo/vector-icons';
import { useRoute, useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import * as Linking from 'expo-linking';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  SafeAreaView,
  StyleSheet,
  Platform,
  Image,
  TextInput,
} from 'react-native';

// Import required services
import MCQMultipleQuestionPremium from '../../../components/quiz/formats/MCQMultipleQuestionPremium';
import MCQSingleQuestionPremium from '../../../components/quiz/formats/MCQSingleQuestionPremium';
import TrueFalseQuestion from '../../../components/quiz/formats/TrueFalseQuestion';
import { supabase } from '../../../lib/supabase';
import { generateReactNativeStyles, KingdomBibleQuizDesign } from '../../../shared/design/UniversalDesignSystem';
import { subscriptionService } from '../../monetization/services/subscriptionService';
import enhancedWhatsAppService from '../services/enhancedWhatsAppService';
// Import question format components (WhatsApp Share supports only basic formats)

// Real API implementation for quiz landing page
const quizLandingAPI = {
  async getQuizSession(sessionId: string, accessToken?: string) {
    try {
      console.log('🔍 Loading quiz session:', { sessionId, hasToken: !!accessToken });

      if (accessToken) {
        // Use secure token access (unauthenticated)
        const { data: tokenData, error: tokenError } = await supabase
          .from('quiz_access_tokens')
          .select('*')
          .eq('access_token', accessToken)
          .eq('is_active', true)
          .gt('expires_at', new Date().toISOString())
          .single();

        if (tokenError || !tokenData) {
          console.error('❌ Invalid access token:', tokenError);
          return {
            success: false,
            error: 'Invalid or expired access token'
          };
        }

        // Get quiz session data
        const { data: sessionData, error: sessionError } = await supabase
          .from('whatsapp_quiz_sessions')
          .select('*')
          .eq('id', sessionId)
          .eq('status', 'active')
          .gt('expires_at', new Date().toISOString())
          .single();

        if (sessionError || !sessionData) {
          console.error('❌ Quiz session not found:', sessionError);
          return {
            success: false,
            error: 'Quiz session not found or expired'
          };
        }

        console.log('✅ Quiz session loaded via token:', sessionData);

        // CRITICAL: Validate sessionData.question_data before using it
        const questionData = sessionData.question_data || {};

        console.log('🔍 Raw question data from database:', {
          questionData,
          hasOptions: !!questionData.options,
          optionsType: typeof questionData.options,
          optionsLength: questionData.options?.length
        });

        // Ensure question_data has valid structure
        const safeQuestionData = {
          id: questionData.id || 'db_question_' + sessionData.id,
          prompt: questionData.prompt || questionData.question || 'Sample question from database',
          options: Array.isArray(questionData.options) && questionData.options.length > 0
            ? questionData.options
            : [
                { id: 'A', label: 'A', text: 'Moses' },
                { id: 'B', label: 'B', text: 'Elijah' },
                { id: 'C', label: 'C', text: 'Jesus' },
                { id: 'D', label: 'D', text: 'Peter' }
              ],
          correctAnswer: questionData.correctAnswer || questionData.correct_answer || 'C',
          explanation: questionData.explanation || 'This is a sample biblical question.',
          questionType: questionData.questionType || questionData.question_type || 'mcq_single'
        };

        return {
          success: true,
          data: {
            sessionId: sessionData.id,
            question: safeQuestionData,
            metadata: sessionData.metadata || {
              title: 'Kingdom Bible Quiz',
              description: 'Test your Bible knowledge!',
              image: 'https://www.kingdombiblequiz.com/images/quiz-card-default.png',
              url: `https://www.kingdombiblequiz.com/app/quiz/${sessionId}`
            },
            groupInfo: sessionData.group_name ? {
              groupId: sessionData.group_id || 'unknown',
              groupName: sessionData.group_name
            } : undefined
          }
        };
      } else {
        // Fallback: Create a sample quiz for testing
        console.log('🧪 Creating sample quiz (no token provided)');

        return {
          success: true,
          data: {
            sessionId,
            question: {
              id: 'sample_q1',
              prompt: 'Which miracle involved the destruction of an army by divine intervention through a prophetic word?',
              options: [
                { id: 'a', label: 'A', text: 'Gideon\'s army defeated by a shout?' },
                { id: 'b', label: 'B', text: 'Elijah\'s fire from heaven?' },
                { id: 'c', label: 'C', text: 'Joshua\'s sun standing still?' },
                { id: 'd', label: 'D', text: 'The destruction of Sodom?' }
              ],
              correctAnswer: 'Gideon\'s army defeated by a shout?',
              explanation: 'God caused the Midianites to flee after Gideon\'s army blew trumpets and shouted, causing confusion (Judges 7:19-22).',
              questionType: 'mcq_single'
            },
            metadata: {
              title: 'Kingdom Bible Quiz - Sample Question',
              description: 'Test your Bible knowledge with this sample question!',
              image: 'https://www.kingdombiblequiz.com/images/quiz-card-default.png',
              url: `https://www.kingdombiblequiz.com/app/quiz/${sessionId}`
            },
            groupInfo: {
              groupId: 'test_group',
              groupName: 'Test Bible Study Group'
            }
          }
        };
      }
    } catch (error) {
      console.error('💥 Failed to load quiz session:', error);
      return {
        success: false,
        error: 'Failed to load quiz session'
      };
    }
  },

  async submitAnswer(sessionId: string, answer: string, timeToAnswer?: number, userAgent?: string, accessToken?: string) {
    try {
      console.log('📝 Submitting answer:', { sessionId, answer, timeToAnswer, hasToken: !!accessToken });

      if (accessToken) {
        // Validate token first
        const { data: tokenData, error: tokenError } = await supabase
          .from('quiz_access_tokens')
          .select('*')
          .eq('access_token', accessToken)
          .eq('is_active', true)
          .gt('expires_at', new Date().toISOString())
          .single();

        if (tokenError || !tokenData) {
          return {
            success: false,
            error: 'Invalid or expired access token'
          };
        }

        // Get quiz data to check answer
        const quizResult = await this.getQuizSession(sessionId, accessToken);
        if (!quizResult.success || !quizResult.data) {
          return {
            success: false,
            error: 'Quiz data not accessible'
          };
        }

        const question = quizResult.data.question;

        // Handle both letter-based and text-based correct answers
        let isCorrect = false;

        // If correctAnswer is a single letter (A, B, C, D)
        if (question.correctAnswer && question.correctAnswer.length === 1) {
          // Find the option that matches the user's answer text
          const selectedOption = question.options.find((opt: any) =>
            opt.text.toLowerCase().trim() === answer.toLowerCase().trim()
          );
          // Compare the option's label/id with the correct answer letter
          isCorrect = selectedOption && (
            selectedOption.label.toUpperCase() === question.correctAnswer.toUpperCase() ||
            selectedOption.id.toUpperCase() === question.correctAnswer.toUpperCase()
          );

          console.log('🔍 Letter-based validation:', {
            userAnswer: answer,
            selectedOption,
            correctAnswerLetter: question.correctAnswer,
            isCorrect
          });
        } else {
          // Full text comparison (original logic)
          isCorrect = answer.toLowerCase().trim() === question.correctAnswer.toLowerCase().trim();

          console.log('🔍 Text-based validation:', {
            userAnswer: answer,
            correctAnswer: question.correctAnswer,
            isCorrect
          });
        }

        // Record the answer submission
        await supabase
          .from('quiz_link_analytics')
          .insert({
            event_type: 'answer_submitted',
            session_id: sessionId,
            metadata: {
              selected_answer: answer,
              is_correct: isCorrect,
              time_to_answer: timeToAnswer,
              timestamp: new Date().toISOString(),
              access_method: 'token_auth',
              user_agent: userAgent
            }
          });

        console.log('✅ Answer submitted successfully:', { isCorrect, correctAnswer: question.correctAnswer });

        return {
          success: true,
          data: {
            isCorrect,
            correctAnswer: question.correctAnswer,
            explanation: question.explanation,
            timeToAnswer
          }
        };
      } else {
        // Sample mode - simulate answer checking
        const sampleQuestion = {
          correctAnswer: 'Gideon\'s army defeated by a shout?',
          explanation: 'God caused the Midianites to flee after Gideon\'s army blew trumpets and shouted, causing confusion (Judges 7:19-22).'
        };

        const isCorrect = answer.toLowerCase().trim() === sampleQuestion.correctAnswer.toLowerCase().trim();

        console.log('🧪 Sample answer check:', { answer, isCorrect });

        return {
          success: true,
          data: {
            isCorrect,
            correctAnswer: sampleQuestion.correctAnswer,
            explanation: sampleQuestion.explanation,
            timeToAnswer
          }
        };
      }
    } catch (error) {
      console.error('💥 Failed to submit answer:', error);
      return {
        success: false,
        error: 'Failed to submit answer'
      };
    }
  },

  async trackQuizAccess(sessionId: string, userAgent?: string, referrer?: string, accessToken?: string) {
    try {
      console.log('📊 Tracking quiz access:', { sessionId, userAgent, referrer, hasToken: !!accessToken });

      // Record access analytics
      await supabase
        .from('quiz_link_analytics')
        .insert({
          event_type: 'quiz_accessed',
          session_id: sessionId,
          user_agent: userAgent || 'Kingdom Bible Quiz Mobile App',
          referrer: referrer || 'direct_link',
          metadata: {
            timestamp: new Date().toISOString(),
            platform: this.detectPlatform(userAgent),
            access_method: accessToken ? 'token_auth' : 'sample_mode'
          }
        });

      console.log('✅ Quiz access tracked successfully');
    } catch (error) {
      console.warn('⚠️ Failed to track quiz access:', error);
    }
  },

  detectPlatform(userAgent?: string): string {
    if (!userAgent) return 'unknown';

    if (userAgent.includes('WhatsApp')) return 'whatsapp';
    if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'ios';
    if (userAgent.includes('Android')) return 'android';
    if (userAgent.includes('Windows')) return 'windows';
    if (userAgent.includes('Mac')) return 'macos';

    return 'web';
  }
};

interface QuizSession {
  sessionId: string;
  question: {
    id: string;
    prompt: string;
    options: Array<{ id: string; label: string; text: string }>;
    correctAnswer: string;
    explanation?: string;
    questionType: string;
  };
  metadata: {
    title: string;
    description: string;
    image: string;
    url: string;
  };
  groupInfo?: {
    groupId: string;
    groupName: string;
  };
}

// WhatsApp Share only supports: MCQ Single, MCQ Multiple, and True/False questions
// Fill Blank and Ordering questions are disabled for sharing due to complexity

// Define styles before component to fix hoisting issue
const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    ...(Platform.OS === 'web' ? {
      minHeight: '100vh' as any,
      width: '100vw' as any,
      position: 'fixed' as any,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'auto' as any,
      zIndex: 1,
    } : {
      flex: 1,
    }),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  debugText: {
    marginTop: 8,
    fontSize: 14,
    color: '#FFFFFF',
    opacity: 0.8,
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorText: {
    marginTop: 16,
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  downloadButton: {
    marginTop: 24,
    borderRadius: 12,
    overflow: 'hidden',
  },
  downloadButtonGradient: {
    paddingVertical: 16,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  downloadButtonText: {
    marginLeft: 8,
    fontSize: 16,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  mainContainer: {
    ...(Platform.OS === 'web' ? {
      width: '100%',
      minHeight: '100%',
      position: 'relative',
    } : {
      flex: 1,
    }),
  },
  scrollView: {
    ...(Platform.OS === 'web' ? {
      width: '100%',
      position: 'relative',
    } : {
      flex: 1,
    }),
  },
  scrollContent: {
    paddingBottom: Platform.OS === 'web' ? 60 : 40,
    ...(Platform.OS === 'web' ? {
      width: '100%',
      minHeight: 'auto',
      height: 'auto',
    } : {
      flexGrow: 1,
    }),
  },
  header: {
    padding: Platform.OS === 'web' ? 32 : 20,
    alignItems: 'center',
    paddingTop: Platform.OS === 'ios' ? 20 : 24,
  },
  logoSection: {
    alignItems: 'center',
    marginBottom: 20,
  },
  logoImageContainer: {
    width: 100,
    height: 100,
    borderRadius: 25,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    marginBottom: 16,
  },
  logoImage: {
    width: 85,
    height: 85,
  },
  logoTextContainer: {
    flex: 1,
  },
  appTitle: {
    fontSize: 28,
    fontWeight: '800',
    color: '#FFFFFF',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  appTagline: {
    fontSize: 14,
    color: '#FFFFFF',
    opacity: 0.9,
    marginTop: 4,
    fontStyle: 'italic',
  },
  verifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(16, 185, 129, 0.15)',
    borderRadius: 15,
    alignSelf: 'center',
  },
  verifiedText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
    marginLeft: 4,
  },
  groupBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginTop: 12,
  },
  groupName: {
    marginLeft: 8,
    fontSize: 14,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  quizCard: {
    marginHorizontal: Platform.OS === 'web' ? 24 : 16,
    marginVertical: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: Platform.OS === 'web' ? 24 : 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 6,
  },
  resultSection: {
    marginTop: 24,
    padding: 20,
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  resultIcon: {
    alignSelf: 'center',
    marginBottom: 12,
  },
  resultTitle: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  correctTitle: {
    color: '#065F46',
  },
  incorrectTitle: {
    color: '#991B1B',
  },
  resultTextSmall: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 22,
  },
  correctText: {
    color: '#047857',
  },
  incorrectText: {
    color: '#B91C1C',
  },
  explanationBox: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    padding: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#F59E0B',
  },
  explanationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#92400E',
    marginBottom: 8,
  },
  explanationText: {
    fontSize: 14,
    color: '#92400E',
    lineHeight: 20,
  },
  premiumSection: {
    marginHorizontal: Platform.OS === 'web' ? 24 : 16,
    marginTop: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 20,
    padding: Platform.OS === 'web' ? 32 : 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  premiumHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  premiumIcon: {
    marginBottom: 12,
  },
  premiumTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: '#1F2937',
    textAlign: 'center',
    marginBottom: 8,
  },
  premiumSubtitle: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 22,
  },
  featuresGrid: {
    marginBottom: 24,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    marginBottom: 8,
  },
  featureIconSmall: {
    marginRight: 16,
  },
  featureTextContainer: {
    flex: 1,
  },
  featureTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  featureDescription: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 18,
  },
  ctaContainer: {
    alignItems: 'center',
  },
  freeTrialButton: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
    marginBottom: 16,
  },
  freeTrialGradient: {
    paddingVertical: 18,
    paddingHorizontal: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  freeTrialText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  freeTrialSubtext: {
    fontSize: 12,
    color: '#FFFFFF',
    opacity: 0.9,
    marginTop: 4,
  },
  // Additional styles
  premiumFeatureTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
    marginTop: 8,
  },
  premiumFeatureDesc: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 4,
  },
  // Clean Professional Premium Features Grid
  premiumFeaturesGrid: {
    width: '100%',
    marginTop: 24,
    marginBottom: 24,
    gap: 16,
  },
  premiumFeatureRow: {
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'space-between',
  },
  premiumFeatureCard: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    ...(Platform.OS === 'web' && { backdropFilter: 'blur(10px)' } as any),
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 3,
  },
  featureIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 2,
  },
  featureContent: {
    alignItems: 'center',
  },
  featureNumber: {
    fontSize: 20,
    fontWeight: '800',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 2,
  },
  featureLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 4,
  },
  featureSubtext: {
    fontSize: 11,
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
    lineHeight: 14,
  },
  storeBadges: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: Platform.OS === 'web' ? 16 : 12,
    marginTop: 20,
    marginBottom: 24,
  },
  storeBadgeButton: {
    width: Platform.OS === 'web' ? 160 : '90%',
    maxWidth: Platform.OS === 'web' ? 160 : 280,
  },
  storeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  storeBadgeIcon: {
    marginRight: 12,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  storeBadgeText: {
    flex: 1,
  },
  storeBadgeSmall: {
    fontSize: 10,
    color: '#FFFFFF',
    opacity: 0.9,
  },
  storeBadgeLarge: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
    marginTop: 2,
  },
  unsupportedQuestionContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 32,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderStyle: 'dashed',
  },
  unsupportedQuestionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  unsupportedQuestionText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 12,
  },
  unsupportedQuestionCTA: {
    fontSize: 14,
    color: '#3B82F6',
    fontWeight: '600',
    textAlign: 'center',
  },
  // Additional missing styles
  errorTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 8,
  },
  questionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  questionNumber: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  resultBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    marginTop: 20,
  },
  resultText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  ctaSection: {
    marginHorizontal: Platform.OS === 'web' ? 24 : 16,
    marginTop: 24,
  },
  premiumBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  premiumBadgeText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#F59E0B',
    marginHorizontal: 8,
  },
  ctaTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 8,
  },
  ctaDescription: {
    fontSize: 16,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 20,
    opacity: 0.9,
  },
  trustSection: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 16,
    padding: 20,
    marginTop: 20,
  },
  trustHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  trustHeaderText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  trustIndicators: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  trustItem: {
    alignItems: 'center',
    flex: 1,
  },
  trustIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  trustNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  trustLabel: {
    fontSize: 12,
    color: '#FFFFFF',
    opacity: 0.8,
    textAlign: 'center',
  },
  trustDivider: {
    width: 1,
    height: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    marginHorizontal: 16,
  },
  socialProofRow: {
    alignItems: 'center',
  },
  socialProofText: {
    fontSize: 14,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 4,
    opacity: 0.9,
  },
});

export const QuizLandingPage: React.FC = () => {
  console.log('🔄 QuizLandingPage component initializing...');

  // React hooks must be called unconditionally at the top level
  const route = useRoute();
  const navigation = useNavigation();
  const currentUrl = Linking.useURL();

  // Initialize universal design system for WhatsApp
  const universalStyles = generateReactNativeStyles('whatsapp');

  // Extract parameters from URL
  const params = route?.params as any;

  // Extract sessionId from route params (from :sessionId in deep link config)
  let sessionId = params?.sessionId;
  // Extract query parameters (token, lang, etc.)
  let accessToken = params?.token;
  let language = params?.lang || 'en';

  // Fallback: Try to parse the current URL if route params are missing
  if ((!sessionId || !accessToken) && currentUrl) {
    try {
      // Extract sessionId from URL path
      const pathMatch = currentUrl.match(/\/app\/quiz\/([^?]+)/);
      if (pathMatch) {
        sessionId = pathMatch[1];
      }

      // Parse query parameters
      const urlObj = new URL(currentUrl);
      accessToken = urlObj.searchParams.get('token') || accessToken;
      language = urlObj.searchParams.get('lang') || language;
    } catch (error) {
      console.warn('Failed to parse URL fallback:', error);
    }
  }

  // React state hooks must be called unconditionally
  const [quizSession, setQuizSession] = useState<QuizSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [answerResult, setAnswerResult] = useState<{
    isCorrect: boolean;
    correctAnswer: string;
    explanation?: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Define functions before useEffect
  const loadQuizSession = async () => {
    if (!sessionId) {
      setError('Invalid quiz link - no session ID found');
      setLoading(false);
      return;
    }

    try {
      const response = await quizLandingAPI.getQuizSession(sessionId, accessToken);

      if (response.success && response.data) {
        // COMPREHENSIVE question data validation and repair
        const session = response.data;

        // Ensure session exists
        if (!session) {
          console.error('❌ No session data received');
          throw new Error('No session data received');
        }

        // Ensure question object exists
        if (!session.question || typeof session.question !== 'object') {
          console.warn('🚨 Missing or invalid question object, creating fallback');
          session.question = {
            id: 'fallback_question',
            prompt: 'Who turned water into wine?',
            options: [],
            correctAnswer: 'C',
            explanation: 'Jesus performed His first recorded miracle in Cana.',
            questionType: 'mcq_single'
          };
        }

        const question = session.question;

        // CRITICAL: Always ensure options exists as array
        if (!question.options || !Array.isArray(question.options) || question.options.length === 0) {
          console.warn('🚨 Invalid question options detected:', {
            options: question.options,
            type: typeof question.options,
            isArray: Array.isArray(question.options)
          });

          // Create comprehensive fallback options
          question.options = [
            { id: 'A', label: 'A', text: 'Moses' },
            { id: 'B', label: 'B', text: 'Elijah' },
            { id: 'C', label: 'C', text: 'Jesus' },
            { id: 'D', label: 'D', text: 'Peter' }
          ];
        }

        // Validate and normalize each option
        try {
          question.options = question.options.map((opt: any, index: number): any => {
            // Handle string options
            if (typeof opt === 'string') {
              const label = String.fromCharCode(65 + index);
              return { id: label, label, text: opt };
            }

            // Handle object options (ensure all required properties)
            if (opt && typeof opt === 'object') {
              return {
                id: opt.id || opt.label || String.fromCharCode(65 + index),
                label: opt.label || opt.id || String.fromCharCode(65 + index),
                text: opt.text || opt.content || opt.option || String(opt) || `Option ${index + 1}`
              };
            }

            // Handle any other type
            const label = String.fromCharCode(65 + index);
            return { id: label, label, text: String(opt) || `Option ${index + 1}` };
          });
        } catch (error) {
          console.error('🚨 Error processing options:', error);
          // Ultimate fallback
          question.options = [
            { id: 'A', label: 'A', text: 'True' },
            { id: 'B', label: 'B', text: 'False' }
          ];
        }

        // Ensure all other required fields exist with fallbacks
        question.id = question.id || 'fallback_question';
        question.prompt = question.prompt || (question as any).question || (question as any).text || 'Who turned water into wine?';
        question.correctAnswer = question.correctAnswer || (question as any).correct_answer || 'C';
        question.explanation = question.explanation || 'Jesus performed His first recorded miracle in Cana.';
        question.questionType = question.questionType || (question as any).question_type || 'mcq_single';

        // Final validation log
        console.log('✅ Question data validated successfully:', {
          hasOptions: !!question.options,
          optionsCount: question.options?.length,
          optionsStructure: question.options?.map((opt: any) => ({ id: opt.id, text: opt.text?.substring(0, 20) + '...' })),
          prompt: question.prompt?.substring(0, 50) + '...',
          type: question.questionType
        });

        setQuizSession(session);
      } else {
        setError(response.error || 'Failed to load quiz');
      }
    } catch (error) {
      console.error('Failed to load quiz session:', error);
      setError('Failed to load quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const trackQuizAccess = async () => {
    if (!sessionId) return;

    try {
      const userAgent = 'Kingdom Bible Quiz Mobile App';
      const referrer = 'whatsapp_share';
      await quizLandingAPI.trackQuizAccess(sessionId, userAgent, referrer, accessToken);
    } catch (error) {
      console.warn('Failed to track quiz access:', error);
    }
  };

  const handleAnswerSelect = async (answer: string) => {
    if (!answer || showResult || submitting) {
      console.log('Answer selection blocked:', { answer, showResult, submitting });
      return;
    }

    console.log('🎯 Answer selected:', {
      answer,
      type: typeof answer,
      correctAnswer: quizSession?.question?.correctAnswer,
      options: quizSession?.question?.options
    });
    setSelectedAnswer(answer);
    setSubmitting(true);

    try {
      const startTime = Date.now();
      const response = await quizLandingAPI.submitAnswer(
        sessionId,
        answer,
        Date.now() - startTime,
        'Kingdom Bible Quiz Mobile App',
        accessToken
      );

      if (response.success && response.data) {
        setAnswerResult({
          isCorrect: response.data.isCorrect,
          correctAnswer: response.data.correctAnswer,
          explanation: response.data.explanation
        });
        setShowResult(true);
      } else {
        // Use web-compatible alert for browsers
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(response.error || 'Failed to submit answer');
        } else {
          Alert.alert('Error', response.error || 'Failed to submit answer');
        }
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      // Use web-compatible alert for browsers
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('Failed to submit answer. Please try again.');
      } else {
        Alert.alert('Error', 'Failed to submit answer. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadApp = () => {
    const appStoreUrl = 'https://apps.apple.com/us/app/kingdom-bible-quiz-app/id6753972354';
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=kingdom.bible.quiz&pcampaignid=web_share';

    Alert.alert(
      'Download Kingdom Bible Quiz',
      'Get the full app to access thousands of Bible questions, solo play, and private multiplayer with friends and family!',
      [
        { text: 'iOS App Store', onPress: () => Linking.openURL(appStoreUrl) },
        { text: 'Google Play Store', onPress: () => Linking.openURL(playStoreUrl) },
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  const handleStartFreeTrial = async () => {
    try {
      // Check if user is logged in
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // User needs to sign up/log in first
        Alert.alert(
          'Sign Up Required',
          'Please create a free account to start your 7-day premium trial!',
          [
            {
              text: 'Sign Up',
              onPress: () => {
                // Navigate to sign up or show sign up modal
                // For web, redirect to sign up page
                if (Platform.OS === 'web') {
                  window.location.href = '/signup?source=free_trial';
                } else {
                  // For mobile, navigate to auth screen
                  (navigation as any).navigate('Auth', {
                    mode: 'signup',
                    source: 'free_trial'
                  });
                }
              }
            },
            {
              text: 'Log In',
              onPress: () => {
                if (Platform.OS === 'web') {
                  window.location.href = '/login?source=free_trial';
                } else {
                  (navigation as any).navigate('Auth', {
                    mode: 'login',
                    source: 'free_trial'
                  });
                }
              }
            },
            { text: 'Cancel', style: 'cancel' }
          ]
        );
        return;
      }

      // Start the trial
      setSubmitting(true);
      const subscription = await subscriptionService.startFreeTrial(user.id);

      Alert.alert(
        '🎉 Trial Started!',
        'Your 7-day free trial has been activated! You now have access to all premium features including:\n\n• 30,000+ Bible questions\n• All 5 question formats\n• Multiplayer modes\n• Live events\n• Progress tracking\n\nNo credit card required. Your trial ends in 7 days.',
        [
          {
            text: 'Get Started',
            onPress: () => {
              // Navigate to the app or download page
              handleDownloadApp();
            }
          }
        ]
      );

      // Track the event
      console.log('Free trial started:', subscription);

    } catch (error: any) {
      console.error('Failed to start trial:', error);

      if (error.message === 'Free trial has already been used') {
        Alert.alert(
          'Trial Already Used',
          'You have already used your free trial. Would you like to view our subscription plans?',
          [
            {
              text: 'View Plans',
              onPress: () => {
                // Navigate to subscription plans
                if (Platform.OS === 'web') {
                  window.location.href = '/pricing';
                } else {
                  (navigation as any).navigate('SubscriptionPlans');
                }
              }
            },
            { text: 'Cancel', style: 'cancel' }
          ]
        );
      } else if (error.message === 'User already has an active subscription') {
        Alert.alert(
          'Already Subscribed',
          'Great news! You already have an active subscription.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert(
          'Error',
          'Unable to start your free trial. Please try again later.',
          [{ text: 'OK' }]
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = () => {
    console.log('🎯 renderQuestion called with:', {
      hasQuizSession: !!quizSession,
      hasQuestion: !!quizSession?.question,
      hasOptions: !!quizSession?.question?.options,
      optionsType: typeof quizSession?.question?.options,
      optionsLength: quizSession?.question?.options?.length
    });

    if (!quizSession || !quizSession.question) {
      console.warn('🚨 No quiz session or question data');
      return (
        <View style={{ padding: 20 }}>
          <Text style={{ textAlign: 'center', color: '#666' }}>Loading question...</Text>
        </View>
      );
    }

    const { question } = quizSession;

    // CRITICAL: Multiple layers of options validation
    let options = question.options;

    // First validation layer
    if (!options || !Array.isArray(options) || options.length === 0) {
      console.error('🚨 CRITICAL: Invalid question options in renderQuestion:', {
        options,
        type: typeof options,
        isArray: Array.isArray(options),
        length: options?.length,
        fullQuestion: question
      });

      // Emergency repair in render function
      options = [
        { id: 'A', label: 'A', text: 'Moses' },
        { id: 'B', label: 'B', text: 'Elijah' },
        { id: 'C', label: 'C', text: 'Jesus' },
        { id: 'D', label: 'D', text: 'Peter' }
      ];

      // Also fix the question object
      question.options = options;
      question.prompt = question.prompt || 'Who turned water into wine?';
      question.correctAnswer = question.correctAnswer || 'Jesus';

      console.log('🔧 Emergency repair applied:', { options });
    }

    // Second validation layer - ensure each option is valid
    try {
      options = options.map((opt: any, index: number): any => {
        if (!opt) {
          return { id: String.fromCharCode(65 + index), label: String.fromCharCode(65 + index), text: `Option ${index + 1}` };
        }
        if (typeof opt === 'string') {
          const label = String.fromCharCode(65 + index);
          return { id: label, label, text: opt };
        }
        return {
          id: opt.id || String.fromCharCode(65 + index),
          label: opt.label || String.fromCharCode(65 + index),
          text: opt.text || String(opt) || `Option ${index + 1}`
        };
      });
    } catch (error) {
      console.error('🚨 Error validating options in render:', error);
      options = [
        { id: 'A', label: 'A', text: 'True' },
        { id: 'B', label: 'B', text: 'False' }
      ];
    }

    // Ensure we have valid string values for all required props
    const questionProps = {
      question: String(question.prompt || 'Question not available'),
      selectedAnswer: String(selectedAnswer || ''),
      showCorrectAnswer: showResult,
      onSelectAnswer: handleAnswerSelect,
      disabled: showResult || submitting,
      currentIndex: 1,
      totalQuestions: 1,
      explanation: showResult && answerResult?.explanation ? String(answerResult.explanation) : undefined,
      correctAnswer: String(answerResult?.correctAnswer || question.correctAnswer || '')
    };

    try {
      // Validate question type and handle edge cases
      const questionType = question.questionType || 'mcq_single';
      console.log('🎯 Rendering question type:', questionType, 'with data:', question);

      // WhatsApp Share only supports basic question formats
      switch (questionType) {
        case 'mcq_single': {
          const mappedOptions = options.map(opt => {
            if (typeof opt === 'string') return opt;
            if (opt && typeof opt === 'object') return opt.text || opt.label || String(opt);
            return String(opt);
          });
          return (
            <MCQSingleQuestionPremium
              {...questionProps}
              options={mappedOptions}
            />
          );
        }
        case 'true_false':
          // For WhatsApp sharing, display as regular MCQ Single with True/False options
          return (
            <MCQSingleQuestionPremium
              {...questionProps}
              options={['True', 'False']}
            />
          );
        case 'mcq_multiple': {
          const formattedOptions = options.map((opt, index) => {
            if (typeof opt === 'string') return { id: index, text: opt, correct: false };
            if (opt && typeof opt === 'object' && opt.text) return { id: index, text: opt.text, correct: (opt as any).correct || false };
            return { id: index, text: String(opt), correct: false };
          });

          // For WhatsApp sharing, display as regular MCQ Single for simplicity
          return (
            <MCQSingleQuestionPremium
              {...questionProps}
              options={formattedOptions.map(opt => opt.text)}
            />
          );
        }
        case 'fill_blank':
        case 'ordering':
          // Unsupported question types for WhatsApp sharing
          return (
            <View style={styles.unsupportedQuestionContainer}>
              <Ionicons name="information-circle" size={48} color="#6B7280" />
              <Text style={styles.unsupportedQuestionTitle}>
                Question Type Not Supported
              </Text>
              <Text style={styles.unsupportedQuestionText}>
                {questionType === 'fill_blank' ? 'Fill-in-the-blank' : 'Ordering'} questions are not available in shared links.
              </Text>
              <Text style={styles.unsupportedQuestionCTA}>
                Download the full app to access all question formats!
              </Text>
            </View>
          );
        default:
          // Default to MCQ Single for unknown types
          return (
            <MCQSingleQuestionPremium
              {...questionProps}
              options={options.map(opt => {
                if (typeof opt === 'string') return opt;
                if (opt && typeof opt === 'object') return opt.text || opt.label || String(opt);
                return String(opt);
              })}
            />
          );
      }
    } catch (error) {
      console.error('Error rendering question component:', error);
      console.error('Question data:', question);
      console.error('Question type:', question?.questionType);

      return (
        <View style={{ padding: 20, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
          <Text style={{ color: '#666', textAlign: 'center', marginBottom: 8 }}>
            Unable to display question. Please try refreshing.
          </Text>
          <Text style={{ color: '#999', textAlign: 'center', fontSize: 12 }}>
            Error: {(error as any)?.message || 'Unknown error'}
          </Text>
          <Text style={{ color: '#999', textAlign: 'center', fontSize: 12 }}>
            Type: {question?.questionType || 'Unknown'}
          </Text>
        </View>
      );
    }
  };

  // useEffect must be called unconditionally
  useEffect(() => {
    const timer = setTimeout(() => {
      loadQuizSession();
      trackQuizAccess();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [sessionId, accessToken]);

  // Initialize enhanced WhatsApp service and PWA features
  useEffect(() => {
    // Initialize Progressive Web App features
    enhancedWhatsAppService.initializeProgressiveWebApp();

    // Generate and cache rich preview data for current session
    if (sessionId) {
      enhancedWhatsAppService.generateRichPreview(sessionId)
        .then((preview) => {
          console.log('📱 Rich preview generated:', preview);
          // Could update meta tags dynamically here if needed
        })
        .catch((error) => {
          console.warn('Failed to generate rich preview:', error);
        });
    }
  }, [sessionId]);

  // Early debug render to show what's happening
  if (loading && (!sessionId || !accessToken)) {
    return (
      <View style={styles.container}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          style={styles.loadingContainer}
        >
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={styles.loadingText}>Parsing Quiz Link...</Text>
          <Text style={styles.debugText}>
            Session ID: {sessionId || 'Not found'}
          </Text>
          <Text style={styles.debugText}>
            Token: {accessToken ? 'Present' : 'Missing'}
          </Text>
          <Text style={styles.debugText}>
            URL: {currentUrl || 'No URL detected'}
          </Text>
        </LinearGradient>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          style={styles.loadingContainer}
        >
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={styles.loadingText}>Loading Quiz...</Text>
        </LinearGradient>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          style={styles.errorContainer}
        >
          <Ionicons name="alert-circle" size={64} color="#FFFFFF" />
          <Text style={styles.errorTitle}>Quiz Not Available</Text>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity
            style={styles.downloadButton}
            onPress={handleDownloadApp}
          >
            <LinearGradient
              colors={['#4F46E5', '#7C3AED']}
              style={styles.downloadButtonGradient}
            >
              <Ionicons name="download" size={20} color="#FFFFFF" />
              <Text style={styles.downloadButtonText}>Get Full App</Text>
            </LinearGradient>
          </TouchableOpacity>
        </LinearGradient>
      </View>
    );
  }

  // Use unified React Native component for all platforms

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={['#667eea', '#764ba2']}
        style={styles.mainContainer}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          showsHorizontalScrollIndicator={false}
          bounces={(Platform.OS as any) !== 'web'}
          overScrollMode={Platform.OS === 'android' ? 'always' : 'auto'}
          nestedScrollEnabled={true}
          keyboardShouldPersistTaps="handled"
          scrollEventThrottle={16}
          removeClippedSubviews={false}
          {...((Platform.OS as any) === 'web' && {
            dataSet: { 'scroll-enabled': 'true' }
          })}
          alwaysBounceVertical={Platform.OS === 'ios'}
          scrollEnabled={true}>
          {/* Premium Header with Logo */}
          <View style={styles.header}>
            {/* Official Logo with Branding */}
            <View style={styles.logoSection}>
              <View style={styles.logoImageContainer}>
                <Image
                  source={require('../../../../assets/kingdom-bible-quiz-logo-transparent.png')}
                  style={styles.logoImage}
                  resizeMode="contain"
                />
              </View>

              <Text style={styles.appTagline}>✨ Grow in Faith Together ✨</Text>

              <View style={styles.verifiedBadge}>
                <Ionicons name="shield-checkmark" size={14} color="#10B981" />
                <Text style={styles.verifiedText}>Official App</Text>
              </View>
            </View>

            {/* Group Info Badge */}
            {quizSession?.groupInfo && (
              <View style={styles.groupBadge}>
                <Ionicons name="people" size={16} color="#FFFFFF" />
                <Text style={styles.groupName}>
                  {quizSession.groupInfo.groupName}
                </Text>
              </View>
            )}
          </View>

          {/* Quiz Card */}
          <View style={styles.quizCard}>
            <View style={styles.questionHeader}>
              <Text style={styles.questionNumber}>Question 1 of 1</Text>
              {submitting && (
                <ActivityIndicator size="small" color="#4F46E5" />
              )}
            </View>

            {renderQuestion()}

            {/* Result Section */}
            {showResult && answerResult && (
              <View style={[
                styles.resultBanner,
                { backgroundColor: answerResult.isCorrect ? '#10B981' : '#EF4444' }
              ]}>
                <Ionicons
                  name={answerResult.isCorrect ? "checkmark-circle" : "close-circle"}
                  size={24}
                  color="#FFFFFF"
                />
                <Text style={styles.resultText}>
                  {answerResult.isCorrect ? 'Correct!' : 'Incorrect!'}
                </Text>
              </View>
            )}
          </View>

          {/* Premium Call to Action */}
          <View style={styles.ctaSection}>
            {/* Premium Badge */}
            <View style={styles.premiumBadge}>
              <Ionicons name="star" size={16} color="#F59E0B" />
              <Text style={styles.premiumBadgeText}>PREMIUM EXPERIENCE</Text>
              <Ionicons name="star" size={16} color="#F59E0B" />
            </View>

            <Text style={styles.ctaTitle}>Continue Your Bible Journey!</Text>
            <Text style={styles.ctaDescription}>
              Join millions of believers growing in faith with Kingdom Bible Quiz
            </Text>

            {/* Premium Features - Clean Professional Grid */}
            <View style={styles.premiumFeaturesGrid}>
              <View style={styles.premiumFeatureRow}>
                <View style={styles.premiumFeatureCard}>
                  <View style={[styles.featureIcon, { backgroundColor: '#6366F1' }]}>
                    <Ionicons name="infinite" size={20} color="#FFFFFF" />
                  </View>
                  <View style={styles.featureContent}>
                    <Text style={styles.featureNumber}>37,000+</Text>
                    <Text style={styles.featureLabel}>Questions</Text>
                    <Text style={styles.featureSubtext}>Unlimited Access</Text>
                  </View>
                </View>

                <View style={styles.premiumFeatureCard}>
                  <View style={[styles.featureIcon, { backgroundColor: '#0EA5E9' }]}>
                    <Ionicons name="apps" size={20} color="#FFFFFF" />
                  </View>
                  <View style={styles.featureContent}>
                    <Text style={styles.featureNumber}>5</Text>
                    <Text style={styles.featureLabel}>Formats</Text>
                    <Text style={styles.featureSubtext}>MCQ, True/False & More</Text>
                  </View>
                </View>
              </View>

              <View style={styles.premiumFeatureRow}>
                <View style={styles.premiumFeatureCard}>
                  <View style={[styles.featureIcon, { backgroundColor: '#10B981' }]}>
                    <Ionicons name="globe" size={20} color="#FFFFFF" />
                  </View>
                  <View style={styles.featureContent}>
                    <Text style={styles.featureNumber}>10</Text>
                    <Text style={styles.featureLabel}>Languages</Text>
                    <Text style={styles.featureSubtext}>Global Community</Text>
                  </View>
                </View>

                <View style={styles.premiumFeatureCard}>
                  <View style={[styles.featureIcon, { backgroundColor: '#F59E0B' }]}>
                    <Ionicons name="trophy" size={20} color="#FFFFFF" />
                  </View>
                  <View style={styles.featureContent}>
                    <Text style={styles.featureNumber}>Live</Text>
                    <Text style={styles.featureLabel}>Events</Text>
                    <Text style={styles.featureSubtext}>Weekly Tournaments</Text>
                  </View>
                </View>
              </View>

              <View style={styles.premiumFeatureRow}>
                <View style={styles.premiumFeatureCard}>
                  <View style={[styles.featureIcon, { backgroundColor: '#EC4899' }]}>
                    <Ionicons name="people" size={20} color="#FFFFFF" />
                  </View>
                  <View style={styles.featureContent}>
                    <Text style={styles.featureNumber}>Multi</Text>
                    <Text style={styles.featureLabel}>player</Text>
                    <Text style={styles.featureSubtext}>Family & Friends</Text>
                  </View>
                </View>

                <View style={styles.premiumFeatureCard}>
                  <View style={[styles.featureIcon, { backgroundColor: '#8B5CF6' }]}>
                    <Ionicons name="school" size={20} color="#FFFFFF" />
                  </View>
                  <View style={styles.featureContent}>
                    <Text style={styles.featureNumber}>Bible</Text>
                    <Text style={styles.featureLabel}>Study</Text>
                    <Text style={styles.featureSubtext}>Group Learning</Text>
                  </View>
                </View>
              </View>
            </View>

            {/* Professional App Store Badges */}
            <View style={styles.storeBadges}>
              <TouchableOpacity
                style={styles.storeBadgeButton}
                onPress={() => Linking.openURL('https://apps.apple.com/us/app/kingdom-bible-quiz-app/id6753972354')}
              >
                <LinearGradient
                  colors={['#000000', '#1A1A1A']}
                  style={styles.storeBadge}
                >
                  <View style={styles.storeBadgeIcon}>
                    <Ionicons name="logo-apple" size={28} color="#FFFFFF" />
                  </View>
                  <View style={styles.storeBadgeText}>
                    <Text style={styles.storeBadgeSmall}>Download on the</Text>
                    <Text style={styles.storeBadgeLarge}>App Store</Text>
                  </View>
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.storeBadgeButton}
                onPress={() => Linking.openURL('https://play.google.com/store/apps/details?id=kingdom.bible.quiz&pcampaignid=web_share')}
              >
                <LinearGradient
                  colors={['#000000', '#1A1A1A']}
                  style={styles.storeBadge}
                >
                  <View style={styles.storeBadgeIcon}>
                    <Ionicons name="logo-google-playstore" size={28} color="#34A853" />
                  </View>
                  <View style={styles.storeBadgeText}>
                    <Text style={styles.storeBadgeSmall}>GET IT ON</Text>
                    <Text style={styles.storeBadgeLarge}>Google Play</Text>
                  </View>
                </LinearGradient>
              </TouchableOpacity>
            </View>

            {/* Enhanced Trust Indicators with Social Proof */}
            <View style={styles.trustSection}>
              <View style={styles.trustHeader}>
                <Ionicons name="shield-checkmark" size={20} color="#10B981" />
                <Text style={styles.trustHeaderText}>Trusted by Believers Worldwide</Text>
              </View>

              <View style={styles.trustIndicators}>
                <View style={styles.trustItem}>
                  <View style={styles.trustIconContainer}>
                    <Ionicons name="star" size={18} color="#F59E0B" />
                  </View>
                  <Text style={styles.trustNumber}>4.8★</Text>
                  <Text style={styles.trustLabel}>App Store Rating</Text>
                </View>
                <View style={styles.trustDivider} />
                <View style={styles.trustItem}>
                  <View style={styles.trustIconContainer}>
                    <Ionicons name="download" size={18} color="#4F46E5" />
                  </View>
                  <Text style={styles.trustNumber}>500K+</Text>
                  <Text style={styles.trustLabel}>Downloads</Text>
                </View>
                <View style={styles.trustDivider} />
                <View style={styles.trustItem}>
                  <View style={styles.trustIconContainer}>
                    <Ionicons name="business" size={18} color="#059669" />
                  </View>
                  <Text style={styles.trustNumber}>10K+</Text>
                  <Text style={styles.trustLabel}>Churches</Text>
                </View>
              </View>

              {/* Additional Social Proof */}
              <View style={styles.socialProofRow}>
                <Text style={styles.socialProofText}>
                  ✅ Featured in Christianity Today
                </Text>
                <Text style={styles.socialProofText}>
                  ✅ Recommended by Pastors
                </Text>
              </View>
            </View>

            {/* Free Trial Button */}
            <TouchableOpacity
              style={styles.freeTrialButton}
              onPress={handleStartFreeTrial}
              disabled={submitting}
            >
              <LinearGradient
                colors={['#4F46E5', '#7C3AED']}
                style={styles.freeTrialGradient}
              >
                {submitting ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <>
                    <Text style={styles.freeTrialText}>Start Free Trial</Text>
                    <Text style={styles.freeTrialSubtext}>No credit card required</Text>
                  </>
                )}
              </LinearGradient>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </LinearGradient>
    </View>
  );
};

export default QuizLandingPage;
